import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'query-string';
import * as Cookie from 'cookie';
import { RootState } from '../../../reducers/reducers';
import { selectOrg } from '../../../selectors/selectors';
import { STAPE_ID_COOKIE_KEY } from '@/constants';
import { hubSpotUserToken } from '@/helpers';
import { UserUpdatable } from '../../../actions/updatable';
import { tryVerifyCommsUser, VerifyCommsUserRequest } from '../../../actions/user';
import { tryChangeOrgFetchState } from '../../../actions/state';
import { globalMessage } from '../../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '../../../utils/hooks/helpers';
import { IntegrationType } from '@/types';
import { displayNameForIntegrationType } from '@/helpers/integrationHelpers';

export const useCommsUserIdVerification: () => void = () => {
    const history = useHistory();

    const location = useLocation();
    const queryParams = qs.parse(location.search);

    const parsedIntegrationType = Object.values(IntegrationType).find(
        (type) => type === queryParams.integrationType,
    ) as IntegrationType;

    const orgId = useSelector((state: RootState) => selectOrg(state).id);
    const userId = useSelector((state: RootState) => state.user.id);

    const verifyIsLoading = useIsLoading(UserUpdatable.VERIFY_COMMS_USER);
    const verifyError = useError(UserUpdatable.VERIFY_COMMS_USER);
    const verifySuccess = useUpdated(UserUpdatable.VERIFY_COMMS_USER);

    const [hasVerifiedCommsUser, setHasVerifiedCommsUser] = React.useState(false);

    let verifyRequest: VerifyCommsUserRequest | undefined;

    if (orgId && queryParams.commsUserId && queryParams.commsUserVerificationCode && queryParams.integrationType) {
        verifyRequest = {
            orgId,
            teamId: queryParams.teamId as string,
            commsUserId: queryParams.commsUserId as string,
            code: queryParams.commsUserVerificationCode as string,
            integrationType: queryParams.integrationType as string,
        };
    }

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (verifyRequest && !hasVerifiedCommsUser) {
            dispatch(tryVerifyCommsUser(verifyRequest));
            setHasVerifiedCommsUser(true);
        }
    }, [verifyRequest, hasVerifiedCommsUser]);

    React.useEffect(() => {
        if (verifyIsLoading) {
            globalMessage.loading({
                content: `Verifying ${displayNameForIntegrationType(parsedIntegrationType)} user...`,
                key: 'verify-comms-user',
                duration: 0,
            });
        } else {
            globalMessage.destroy('verify-comms-user');
        }
    }, [verifyIsLoading]);

    React.useEffect(() => {
        if (verifySuccess) {
            globalMessage.success(
                `Verified ${displayNameForIntegrationType(parsedIntegrationType)} user successfully!`,
            );

            history.replace({ search: '' });
            const cookies = Cookie.parse(document.cookie);
            dispatch(
                tryChangeOrgFetchState({
                    orgId: orgId as unknown as number,
                    userId,
                    hubspotUserToken: hubSpotUserToken(document),
                    stapeId: cookies[STAPE_ID_COOKIE_KEY],
                }),
            );
        }
    }, [verifySuccess, history, dispatch, orgId, userId]);

    React.useEffect(() => {
        if (verifyError) {
            globalMessage.error(verifyError);
        }
    }, [verifyError]);
};
